import { fabric } from 'fabric';

export const initFabricHistory = () => {
    if (fabric.Canvas.prototype._historyInit) return;

    const originalInitialize = fabric.Canvas.prototype.initialize;
    fabric.Canvas.prototype._applyObjectStyles = applyObjectStyles;

    fabric.Canvas.prototype.initialize = function (el, options) {
        const result = originalInitialize.call(this, el, options);
    
        if (!el?.parentNode) return result;
    
        const canvasContainer = el.parentNode;
        canvasContainer.style.position = 'relative';
    
        const bgContainer = document.createElement('div');
        Object.assign(bgContainer.style, {
            position:   'absolute',
            top:        '0',
            left:       '0',
            width:      '100%',
            height:     '100%',
            zIndex:     '-1'
        });
        bgContainer.className = 'fabric-bg-container';
    
        canvasContainer.insertBefore(bgContainer, el);
        this._backgroundImgContainer = bgContainer;
    
        return result;
    };

    fabric.Canvas.prototype._historyInit = function () {
        this.historyUndo        = [];
        this.historyRedo        = [];
        this._historyRecording  = true;
        this._mouseDownActive   = false;

        this._initialState = this.toDatalessJSON([
            'uuid',
            'uuid_parent',
            'id',
            'name',
            'labeled',
            'stroke',
            'objid',
            'color',
            'elementType',
            'scaleX',
            'scaleY',
            'left',
            'top',
            'strokeWidth',
            '_isEraserPath',
            'clipPath',
            'cornerColor',
            'cornerStyle',
            'globalCompositeOperation'
        ]);

        this.historyUndo.push(this._initialState);

        this.on('object:added', (e) => {
            if (!e.target.excludeFromExport) {
                this._pushHistory();
            }
        });

        this.on('object:modified', (e) => {
            if (!e.target.excludeFromExport) {
                this._pushHistory();
            }
        });

        this.on('object:removed', (e) => {
            if (!e.target.excludeFromExport) {
                this._pushHistory();
            }
        });
        
        this.on('path:created', (e) => {
            if (this.freeDrawingBrush && this.freeDrawingBrush.globalCompositeOperation === 'destination-out') {
                e.path._isEraserPath = true;
                e.path.set({ globalCompositeOperation: 'destination-out' });
            }
        });

        this.on('mouse:down', (e) => {
            if (!e.target && e.e && e.e.type === 'mousedown' && !this.isFreeDrawing) {
                this._mouseDownActive = true;
            }
        });

        this.on('mouse:up', (e) => {
            if (this._mouseDownActive) {
                this._mouseDownActive = false;
                this._historyRecording = true;
                this._pushHistory();
            }
        });
    };

    fabric.Canvas.prototype._pushHistory = function () {
        if (!this._historyRecording) return;

        const currentState = this.toDatalessJSON([
            'uuid',
            'uuid_parent',
            'id',
            'name',
            'labeled',
            'stroke',
            'objid',
            'color',
            'elementType',
            'scaleX',
            'scaleY',
            'left',
            'top',
            'strokeWidth',
            '_isEraserPath',
            'eraserPath', 
            'cornerColor',
            'cornerStyle',
            'globalCompositeOperation'
        ]);

        const modObjects = currentState.objects.filter(obj => obj.elementType !== "background" && !obj.excludeFromExport);

        const modState = { objects: modObjects };
        const lastState = this.historyUndo[this.historyUndo.length - 1];

        if (!lastState || JSON.stringify(modState) !== JSON.stringify(lastState)) {
            if (this.isDrawingMode && this.freeDrawingBrush && this.freeDrawingBrush.globalCompositeOperation === 'destination-out') {
                this.historyUndo.push(modState);
            } 
            else if (this.isDrawingMode && this.historyUndo.length > 1) {
                const currentUUIDs  = new Set(modObjects.map(obj => obj.uuid));
                const lastUUIDs     = new Set((lastState.objects || []).map(obj => obj.uuid));
                const newUUIDs      = [...currentUUIDs].filter(uuid => !lastUUIDs.has(uuid));
        
                const modifiedObjects = modObjects.filter(modObj => {
                    const lastObj = (lastState.objects || []).find(o => o.uuid === modObj.uuid);
                    return lastObj && (
                        modObj.name !== lastObj.name ||
                        modObj.stroke !== lastObj.stroke ||
                        modObj.color !== lastObj.color
                    );
                });
        
                if (newUUIDs.length > 0 || modifiedObjects.length > 0) {
                    this.historyUndo.push(modState);
                } else {
                    this.historyUndo[this.historyUndo.length - 1] = modState;
                }
            } else {
                this.historyUndo.push(modState);
            }
        
            this.historyRedo = [];
        
            if (this._onHistoryChange && typeof this._onHistoryChange === 'function') {
                this._onHistoryChange();
            }
        }
        
    };

    fabric.Canvas.prototype._clearHistory = function () {
        this.historyUndo = [this.toDatalessJSON([
            'uuid',
            'uuid_parent',
            'id',
            'name',
            'labeled',
            'stroke',
            'objid',
            'color',
            'elementType'
        ])];
        this.historyRedo = [];
    };

    fabric.Canvas.prototype.undo = function (callback) {

        if (this.historyUndo.length <= 1) return;

        this._historyRecording = false;
        
        const lastState = this.historyUndo.pop();
        this.historyRedo.push(lastState);
        
        const prevState = this.historyUndo[this.historyUndo.length - 1];
        
        const pathProperties = new Map();
        this.getObjects().filter(obj => obj.type === 'path' && obj.elementType !== 'background' && obj.elementType !== 'crosshair').forEach(path => {
            pathProperties.set(path.uuid || path.objid || path.id, {
                scaleX:                     path.scaleX,
                scaleY:                     path.scaleY,
                left:                       path.left,
                top:                        path.top,
                strokeWidth:                path.strokeWidth,
                _isEraserPath:              path._isEraserPath,
                globalCompositeOperation:   path.globalCompositeOperation
            });
        });
        
        const objectsToRemove = this.getObjects().filter(obj => obj.elementType !== 'background' && obj.elementType !== 'crosshair');
        objectsToRemove.forEach(obj => this.remove(obj));
        
        const stateToLoad = mergeState(this._initialState, prevState, this);
        
        this.loadFromJSON(stateToLoad, () => {
            this.getObjects().filter(obj => 
                obj.type === 'path' && 
                (obj.uuid || obj.objid || obj.id) && 
                pathProperties.has(obj.uuid || obj.objid || obj.id)
            ).forEach(path => {
                const props = pathProperties.get(path.uuid || path.objid || path.id);
                if (props) {
                    path.set({
                        scaleX:         props.scaleX,
                        scaleY:         props.scaleY,
                        left:           props.left,
                        top:            props.top,
                        strokeWidth:    props.strokeWidth
                    });
                    
                    if (props._isEraserPath || props.globalCompositeOperation === 'destination-out') {
                        path._isEraserPath = true;
                        path.set({ globalCompositeOperation: 'destination-out' });
                    }
                }
            });
            
            this._applyObjectStyles();
            this.renderAll();
            this._historyRecording = true;
            
            if (callback) callback();
        });
    };

    fabric.Canvas.prototype.redo = function (callback) {
        if (this.historyRedo.length === 0) return;

        this._historyRecording = false;
        
        const nextState = this.historyRedo.pop();
        this.historyUndo.push(nextState);
        
        const pathProperties = new Map();
        this.getObjects().filter(obj => obj.type === 'path' && obj.elementType !== 'background' && obj.elementType !== 'crosshair').forEach(path => {
            pathProperties.set(path.uuid || path.objid || path.id, {
                scaleX:                     path.scaleX,
                scaleY:                     path.scaleY,
                left:                       path.left,
                top:                        path.top,
                strokeWidth:                path.strokeWidth,
                _isEraserPath:              path._isEraserPath,
                globalCompositeOperation:   path.globalCompositeOperation
            });
        });
        
        const objectsToRemove = this.getObjects().filter(obj => obj.elementType !== 'background' &&  obj.elementType !== 'crosshair');
        
        objectsToRemove.forEach(obj => this.remove(obj));
        
        const stateToLoad = mergeState(this._initialState, nextState, this);
        
        this.loadFromJSON(stateToLoad, () => {
            this.getObjects().filter(obj => 
                obj.type === 'path' && 
                (obj.uuid || obj.objid || obj.id) && 
                pathProperties.has(obj.uuid || obj.objid || obj.id)
            ).forEach(path => {
                const props = pathProperties.get(path.uuid || path.objid || path.id);
                if (props) {
                    path.set({
                        scaleX:         props.scaleX,
                        scaleY:         props.scaleY,
                        left:           props.left,
                        top:            props.top,
                        strokeWidth:    props.strokeWidth
                    });
                    
                    if (props._isEraserPath || props.globalCompositeOperation === 'destination-out') {
                        path._isEraserPath = true;
                        path.set({ globalCompositeOperation: 'destination-out' });
                    }
                }
            });
            
            this._applyObjectStyles();
            this.renderAll();
            this._historyRecording = true;

            if (callback) callback();
        });
    };
};

const mergeState = function (baseState, modState, canvas) {
    const base = typeof baseState === 'string' ? JSON.parse(baseState) : baseState;
    
    if (!modState || !modState.objects || modState.objects.length === 0) {
        return base;
    }
    
    const currentBackgrounds = canvas.getObjects().filter(obj => obj.elementType === 'background');
    
    const preservedBackgrounds = currentBackgrounds.length > 0
        ? currentBackgrounds
        : base.objects.filter(obj => (obj.elementType === 'background' || obj.initial) && !obj.excludeFromExport);
    
    const baseHasPaths = base.objects && base.objects.some(obj => obj.type === 'path');
    const modHasPaths = modState.objects && modState.objects.some(obj => obj.type === 'path');
    
    if (baseHasPaths && !modHasPaths) {
        const basePaths = base.objects.filter(obj => obj.type === 'path');
        const nonPathModObjects = modState.objects.filter(obj => obj.type !== 'path');
        return { ...base, objects: [...preservedBackgrounds, ...nonPathModObjects, ...basePaths] };
    }
    
    if (modHasPaths) {
        const modObjects = modState.objects.map(obj => {
            if (obj.type === 'path') {
                return {
                    ...obj,
                    scaleX: obj.scaleX || 1,
                    scaleY: obj.scaleY || 1,
                    left: obj.left !== undefined ? obj.left : (obj.left || 0),
                    top: obj.top !== undefined ? obj.top : (obj.top || 0),
                    strokeWidth: obj.strokeWidth !== undefined ? obj.strokeWidth : obj.strokeWidth,
                    globalCompositeOperation: obj._isEraserPath ? 'destination-out' : (obj.globalCompositeOperation || 'source-over')
                };
            }
            return obj;
        });
        
        return { ...base, objects: [...preservedBackgrounds, ...modObjects] };
    }
    
    return { ...base, objects: [...preservedBackgrounds, ...modState.objects] };
};

const applyObjectStyles = function () {
    this.getObjects().forEach(obj => {
        const isDrawingMode = this.isDrawingMode;
        if (obj.elementType === 'background') {
            obj.set({ selectable: false, evented: false, hoverCursor: 'crosshair' });
        } else if (obj.type === 'rect' || obj.type === 'path') {
            if (obj.setControlsVisibility) {
                obj.setControlsVisibility({
                    mt: false,  mb: false, ml: false, mr: false,
                    bl: true,   br: true, tl: true, tr: true, mtr: false
                });
            }
            
            obj.set({
                selectable:     !isDrawingMode,
                evented:        !isDrawingMode,
                hoverCursor:    isDrawingMode ? 'crosshair' : 'move'
            });
            
            if (obj.type === 'path') {
                obj.set({
                    perPixelTargetFind: true,
                    strokeUniform:      true,
                    scaleX:             obj.scaleX || 1,
                    scaleY:             obj.scaleY || 1,
                    left:               obj.left || 0,
                    top:                obj.top || 0
                });
                
                if (obj._isEraserPath) {
                    obj.set({
                        globalCompositeOperation: 'destination-out'
                    });
                }
            }
        }
    });
};